import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Target, Zap, TrendingUp } from 'lucide-react';

const whatWeDo = [
  {
    icon: Zap,
    title: 'Product & Platform Development',
    description: 'Building products and platforms for music and media businesses.',
  },
  {
    icon: Target,
    title: 'Technology & Infrastructure',
    description: 'Technology and infrastructure for distribution and rights management.',
  },
  {
    icon: TrendingUp,
    title: 'Music Ecosystem Services',
    description: 'Services across licensing, partnerships, and monetization.',
  },
  {
    icon: Calendar,
    title: 'Strategic Execution',
    description: 'Strategic execution for growth, expansion, and market entry.',
  },
];

const AboutPage = () => {
  return (
    <div className="page-transition" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-richblack-light" data-testid="about-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="gold-line mb-6" />
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              About <span style={{ color: '#D4AF37' }}>Us</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              Technology-led innovation meets deep music industry expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="section-padding bg-richblack" data-testid="about-intro">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-xl text-white/80 leading-relaxed mb-8">
              Eaglence is a technology-led company built by industry professionals with over a decade of experience across digital infrastructure and the global music ecosystem.
            </p>
            <div className="flex items-center gap-4 mb-8">
              <div className="gold-line" />
              <span className="text-gold font-sans text-sm tracking-wider uppercase">Founded in 2024</span>
            </div>
            <p className="text-white/60 leading-relaxed">
              We operate at the intersection of technology and music—combining strong technical capabilities with a deep understanding of music distribution, licensing, partnerships, and content monetization. Our experience across the music value chain allows us to approach challenges with both strategic clarity and executional depth.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding bg-richblack-light" data-testid="what-we-do">
        <div className="container-custom">
          <div className="mb-16">
            <div className="gold-line mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              What We Do
            </h2>
            <p className="text-white/60 max-w-2xl">
              We build and support systems that power modern music businesses:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whatWeDo.map((item, index) => (
              <div 
                key={item.title}
                className="bg-richblack p-8 border-l-2 border-gold"
                data-testid={`what-we-do-${index}`}
              >
                <item.icon className="text-gold mb-4" size={32} strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-white mb-3">{item.title}</h3>
                <p className="text-white/50 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="section-padding bg-richblack" data-testid="approach-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="gold-line mb-6" />
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Our Approach
              </h2>
              <p className="text-2xl text-gold font-serif italic mb-8">
                We are execution-focused.
              </p>
              <p className="text-white/60 leading-relaxed">
                With hands-on experience in both technology and the music industry, we work closely with partners to build scalable solutions that drive real outcomes—bridging the gap between platforms, rights holders, and global markets.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80"
                alt="Music Studio"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 border border-gold/20 translate-x-4 translate-y-4 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Looking Ahead Section */}
      <section className="section-padding bg-richblack-light" data-testid="looking-ahead">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Looking Ahead
            </h2>
            <p className="text-xl text-white/70 leading-relaxed mb-8">
              As the music industry continues to evolve, Eaglence is focused on enabling the next generation of music businesses—through technology, partnerships, and scalable infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-richblack" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Let's Build Together
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-10">
            Ready to explore how Eaglence can support your music business?
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-flex items-center gap-2"
            data-testid="about-contact-btn"
          >
            Get in Touch
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
